import React, { useEffect, useState } from 'react';
import {
  productOfferVariants,
  useProductsContext,
} from '../../contexts/ProductsContext';
import { usePopupContext } from '../../contexts/Settings';
import { useSharedContext } from '../../contexts/SharedContext';
import { getDiscountedPrice } from '../../dissolve/helpers';
import { REGISTERED_APPS } from '../../shared/types/Apps';
import {
  AddButtonAction,
  Offer,
  popupLayoutCssPostfix,
  Product,
  ProductVariant,
} from '../../types';
import { getAddedOffers } from '../../utils/candycart/products';
import {
  AddToCartButton,
  Select,
  SkeletonOffer,
  UpgradeButton,
} from '../elements';
import { BACKEND_MAX_VALUE } from '../elements/OfferQuantity';
import { ProductOfferView } from './ProductOfferView';

export default function SharedLastUpsellOfferItem({
  product,
  offer,
  currency,
  indexInOffers,
  offerCount,
  handleAddToCart,
  handleRemoveFromCart,
}: {
  product?: Product | null;
  offer: Offer;
  currency: string;
  indexInOffers?: number;
  handleAddToCart: (
    offer: Offer,
    product?: Product,
    action?: AddButtonAction,
  ) => Promise<boolean>;
  offerCount: number;
  handleRemoveFromCart: (offer: Offer, variant?: number) => Promise<void>;
}) {
  const { resetSelectedProduct } = useProductsContext();
  const { runningAppName } = useSharedContext();

  const [added, setAdded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [variant, setVariant] = useState<ProductVariant>();
  const [quantity, setQuantity] = useState(
    Math.min(BACKEND_MAX_VALUE, offer.maxQuantityComputedInFE || 1),
  );
  const [offerNote, setOfferNote] = useState<string>();

  const filteredProductVariantsFromOffer = productOfferVariants(product, offer);

  const AcceptOfferButton = offer.is_upgrade ? UpgradeButton : AddToCartButton;

  useEffect(() => {
    const variant = productOfferVariants(product, offer).find(
      (v) => v.available,
    );

    const handleCandyCartAlreadyAddedOffer = () => {
      const addedOffers = getAddedOffers();
      const alreadyAddedVariant = productOfferVariants(product, offer).find(
        (v) =>
          v.available &&
          addedOffers.find(
            (o) => o.shopify_product_variant_id === v.id && o.id === offer.id,
          ),
      );
      const alreadyAddedOffer = addedOffers.find(
        (o) =>
          o.shopify_product_variant_id === alreadyAddedVariant?.id &&
          o.id === offer.id,
      );
      if (alreadyAddedVariant && alreadyAddedOffer) {
        setVariant(alreadyAddedVariant);
        setAdded(true);
        setQuantity(alreadyAddedOffer.quantity);
        setOfferNote(alreadyAddedOffer.note);
      }
    };

    setVariant(variant);

    // Set already added for Candycart Product
    if (runningAppName === REGISTERED_APPS.CANDYCART && product) {
      handleCandyCartAlreadyAddedOffer();
    }
  }, [offer, product, runningAppName]);

  const handleOfferVariantChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setVariant(
      product?.variants.find((v) => v.id === Number(event.target.value)),
    );
  };

  const handleRemoveFromCartClick = async () => {
    setAdded(false);
    setLoading(true);
    await handleRemoveFromCart(offer, variant?.id);
    setLoading(false);
  };

  const handleAddToCartClick = async () => {
    setLoading(true);

    if (!variant || !product) {
      return;
    }

    resetSelectedProduct();

    // Store variant id we should add to cart
    const newOffer: Offer = {
      ...offer,
      shopifyProductVariantIdComputedInFE: variant.id,
      shopifyProductHandleComputedInFE: product.handle,
      quantityComputedInFE: quantity,
      noteComputedInFE: offerNote || '',
    };
    product.discountedPrice = getDiscountedPrice(newOffer, variant, quantity);

    // variant.id is used to show correct image when upgrading
    const productAdded = await handleAddToCart(newOffer, product);
    setAdded(productAdded);

    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: product.title,
        content_ids: [product.id],
        content_type: 'product_group',
        value: Number(variant.price) / 100,
        currency,
      });
    }
    setLoading(false);
  };

  const { settings } = usePopupContext();

  const isProductUnavailable =
    product && !product.loading && (!product.available || !variant);

  if (isProductUnavailable) {
    return null;
  }

  if (!product || !variant) {
    return (
      <SkeletonOffer indexInOffers={indexInOffers} offerCount={offerCount} />
    );
  }

  const popupLayoutCssPostfixValue: string = popupLayoutCssPostfix(settings);
  let primaryButton = (
    <AcceptOfferButton
      disabled={added || loading}
      onClick={handleAddToCartClick}
      data-cy="add"
      className={`Button-Add${popupLayoutCssPostfixValue} ${
        added ? `Button-Add-Added${popupLayoutCssPostfixValue}` : ''
      }`}
    >
      {added ? settings.text.added : settings.text.add}
    </AcceptOfferButton>
  );

  const select = (className: string) =>
    filteredProductVariantsFromOffer.length > 1 ? (
      <Select
        className={className}
        disabled={added}
        onChange={handleOfferVariantChange}
        value={variant.id}
      >
        {filteredProductVariantsFromOffer.map((v) => (
          <option key={v.title} value={v.id} disabled={!v.available}>
            {v.title}
          </option>
        ))}
      </Select>
    ) : null;

  return (
    <ProductOfferView
      added={added}
      handleOfferQuantityChange={setQuantity}
      handleRemoveFromCartClick={handleRemoveFromCartClick}
      loading={loading}
      offer={offer}
      offerCount={offerCount}
      indexInOffers={indexInOffers}
      product={product}
      quantity={quantity}
      primaryButton={primaryButton}
      select={select}
      setOfferNote={setOfferNote}
      variant={variant}
      offerNote={offerNote}
      filteredProductVariantsFromOffer={filteredProductVariantsFromOffer}
    />
  );
}
